/**
 * Component schemas (SPEC §4.2): data-only components described by reflection.
 *
 * A schema is plain data. `defineComponent` only exists for inference: `const`
 * generics keep enum options and flags literal, so `InferComponentData` can
 * derive the exact TypeScript type of a component's data from its schema. Code
 * reading components gets typed data with no hand-written interface to drift.
 */
import type { Rgb } from "../../core/color";
import type { Vec3 } from "../../core/math/vec3";
import type { ColorValue, PropType } from "./propTypes";

export interface ComponentDisplay {
  /** Inspector section order (lower first). */
  readonly order: number;
  /** Type column label in the Outliner when this is the entity's primary component. */
  readonly outlinerType?: string;
  /** Outliner icon name. */
  readonly icon?: string;
}

/** How the editor may add and remove a component (Add Component, Remove component). */
export interface ComponentRules {
  /** Can't be removed: every entity keeps it once it has it (Transform, a level's WorldSettings). */
  readonly required?: boolean;
  /** One per level; the first found wins (level-wide settings). Offered only while the level has none. */
  readonly perLevel?: boolean;
  /** Components the same entity must have first (an Animator plays its Mesh Renderer's model). */
  readonly requires?: readonly string[];
  /** The parent must have one of these (a World UI panel shows its parent's Role or Project). */
  readonly parentNeedsOneOf?: readonly string[];
  /**
   * Can be turned off (Unity's enabled checkbox). Only components that do
   * something (draw, light, animate); core and content data can't.
   */
  readonly toggleable?: boolean;
}

export interface ComponentSchema {
  /** Registry key and manifest key, e.g. `Transform`. */
  readonly type: string;
  /** Inspector section title. */
  readonly category: string;
  readonly display: ComponentDisplay;
  readonly props: Readonly<Record<string, PropType>>;
  /**
   * Keys that arrive with bound seed data but aren't component props (e.g. a
   * seed zone's `id`). They're skipped without a warning, so whole-object seed
   * refs stay clean and can't drift from the seed.
   */
  readonly ignoredKeys?: readonly string[];
  readonly rules?: ComponentRules;
}

/** Stored component data: immutable, JSON-serializable prop values. */
export type ComponentData = Readonly<Record<string, unknown>>;

export function defineComponent<const S extends ComponentSchema>(schema: S): S {
  return schema;
}

/** The TypeScript type of a component's data, derived from its schema. */
export type InferComponentData<S extends ComponentSchema> = {
  readonly [K in keyof S["props"]]: PropValue<S["props"][K]>;
};

/** The TypeScript type of a single prop's value. */
export type PropValue<P extends PropType> = P extends { readonly optional: true }
  ? BaseValue<P> | undefined
  : BaseValue<P>;

type BaseValue<P extends PropType> = P extends { type: "string" | "text" }
  ? string
  : P extends { type: "number" }
    ? number
    : P extends { type: "bool" }
      ? boolean
      : P extends { type: "vec3" }
        ? Vec3
        : P extends { type: "color" }
          ? ColorValue
          : P extends { type: "enum"; options: readonly (infer O)[] }
            ? O extends { value: infer V }
              ? V
              : never
            : P extends { type: "yearMonth" }
              ? P extends { nullable: true }
                ? string | null
                : string
              : P extends { type: "array"; of: infer Of extends PropType }
                ? readonly BaseValue<Of>[]
                : P extends { type: "assetRef" | "entityRef" }
                  ? string | null
                  : P extends { type: "clipRef" }
                    ? string
                    : never;

export type { ColorValue, Rgb };
